import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { getActivePromotions, getProducts } from '../services/api';
import './Home.css';

const CATEGORIES = ['Electronics', 'Clothing', 'Books', 'Home & Garden', 'Sports', 'Toys', 'Collectibles', 'Other'];
const FAQS = [
  {
    question: 'What is Anora Store?',
    answer: 'Anora Store is our curated online shop for fashion merch, collectibles, accessories, and standout everyday finds with secure checkout and tracked delivery.',
  },
  {
    question: 'What does our store have?',
    answer: 'We carry curated apparel, collectibles, accessories, giftable items, and rotating specialty drops depending on what is currently in stock.',
  },
  {
    question: 'Do you offer international shipping?',
    answer: 'Yes. We can ship internationally on many orders, and live shipping options will show at checkout whenever the destination is supported.',
  },
  {
    question: 'How do I track my order?',
    answer: 'After your order ships, you will receive a tracking number. You can also use the Track Order page on the site any time to check shipment progress.',
  },
  {
    question: 'What is your return policy?',
    answer: 'We offer a 30-day free return policy on eligible orders. If something arrives damaged or there is an issue with your order, contact us and we will help with the next best step for a return, exchange, or resolution.',
  },
  {
    question: 'How do I contact customer service?',
    answer: 'Use the support page on the site or contact customer service at 561-285-1789 for order questions, shipping help, or product support.',
  },
];
const TRUST_POINTS = [
  {
    icon: '◎',
    title: 'Worldwide shipping',
    description: 'We ship to over 200 countries when service is available.',
  },
  {
    icon: '◌',
    title: 'Shop with confidence',
    description: 'Protected ordering from click to delivery.',
  },
  {
    icon: '◍',
    title: '30-day free returns',
    description: 'Eligible orders can be returned within 30 days at no extra return cost.',
  },
  {
    icon: '▭',
    title: '100% secure checkout',
    description: 'PayPal, Mastercard, Visa, and Stripe protected payments.',
  },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const sort = searchParams.get('sort') || 'newest';

  useEffect(() => {
    setLoading(true);
    getProducts({ search, category, sort })
      .then(r => setProducts(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [search, category, sort]);

  useEffect(() => {
    getActivePromotions()
      .then((r) => setPromotions(r.data))
      .catch(() => setPromotions([]));
  }, []);

  const setParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  return (
    <div>
      {/* Hero */}
      <div className="hero">
        <div className="hero-noise" />
        <div className="hero-bg-grid">
          <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=70&fit=crop" alt="" />
          <img src="https://images.unsplash.com/photo-1593079831268-3381b0db4a77?w=400&q=70&fit=crop" alt="" />
          <img src="https://images.unsplash.com/photo-1445205170230-053b83016050?w=400&q=70&fit=crop" alt="" />
          <img src="https://images.unsplash.com/photo-1545454675-3531b543be5d?w=400&q=70&fit=crop" alt="" />
          <img src="https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=400&q=70&fit=crop" alt="" />
          <img src="https://images.unsplash.com/photo-1489987707025-afc232f7ea0f?w=400&q=70&fit=crop" alt="" />
        </div>
        <div className="hero-overlay" />
        <div className="hero-content container">
          <div className="hero-eyebrow">Curated finds. Real inventory. Fast dispatch.</div>
          <h1>Shop standout pieces without digging through clutter.</h1>
          <p>Anora Store brings together clean tech, collectible gems, and everyday upgrades with trusted checkout and live shipping.</p>
          <div className="hero-actions">
            <button className="btn btn-accent" onClick={() => setParam('category', '')}>Browse all items</button>
            <button className="btn btn-ghost-light" onClick={() => setParam('category', 'Collectibles')}>Explore collectibles</button>
          </div>
          <div className="hero-stats">
            <div><strong>Secure</strong><span>Stripe and PayPal checkout</span></div>
            <div><strong>Live rates</strong><span>Shipping choices before you pay</span></div>
            <div><strong>Support</strong><span>Track orders and get help fast</span></div>
          </div>
        </div>
      </div>

      {/* Category Cards */}
      <div className="container">
        <div className="category-cards">
          <div className="cat-card" onClick={() => setParam('category', 'Electronics')}>
            <img src="https://images.unsplash.com/photo-1593079831268-3381b0db4a77?w=300&q=70&fit=crop" alt="Amplifiers" />
            <span>Amplifiers & Receivers</span>
          </div>
          <div className="cat-card" onClick={() => setParam('category', 'Clothing')}>
            <img src="https://images.unsplash.com/photo-1445205170230-053b83016050?w=300&q=70&fit=crop" alt="Clothing" />
            <span>Clothing</span>
          </div>
          <div className="cat-card" onClick={() => setParam('category', 'Other')}>
            <img src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&q=70&fit=crop" alt="Shipping" />
            <span>All Items</span>
          </div>
          <div className="cat-card" onClick={() => setParam('category', 'Collectibles')}>
            <img src="https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=300&q=70&fit=crop" alt="Collectibles" />
            <span>Collectibles</span>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '2rem 1.5rem' }}>
        <div className="catalog-intro">
          <div>
            <span className="catalog-kicker">Catalog</span>
            <h2>Fresh inventory with room to browse.</h2>
          </div>
          <p>Filter by category, sort by price, and jump straight into the pieces that match what you are hunting for.</p>
        </div>

        {promotions.length > 0 && (
          <section className="promo-rail">
            {promotions.slice(0, 3).map((promotion) => (
              <div key={promotion.id} className="promo-rail-card">
                <span className="promo-rail-badge">{promotion.discount_percent}% off</span>
                <strong>{promotion.name}</strong>
                <p>{promotion.headline || promotion.description || 'A live promotion is active now.'}</p>
              </div>
            ))}
          </section>
        )}

        {/* Filters */}
        <div className="filters">
          <div className="filter-categories">
            <button
              className={`filter-cat ${!category ? 'active' : ''}`}
              onClick={() => setParam('category', '')}
            >
              All
            </button>
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`filter-cat ${category === cat ? 'active' : ''}`}
                onClick={() => setParam('category', cat)}
              >
                {cat}
              </button>
            ))}
          </div>

          <select
            className="sort-select"
            value={sort}
            onChange={e => setParam('sort', e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
          </select>
        </div>

        {search && (
          <div className="search-result-label">
            Results for "<strong>{search}</strong>"
            <button onClick={() => setParam('search', '')} className="clear-search">✕ Clear</button>
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>No items found</h3>
            <p>Try adjusting your filters or search terms.</p>
          </div>
        ) : (
          <div className="products-grid">
            {products.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        <section className="faq-section">
          <div className="faq-header">
            <span className="catalog-kicker">Questions</span>
            <h2>Answers customers ask before they order.</h2>
          </div>
          <div className="faq-list">
            {FAQS.map((faq, index) => (
              <div key={faq.question} className={`faq-item ${openFaq === index ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => setOpenFaq(openFaq === index ? -1 : index)}>
                  <span>{faq.question}</span>
                  <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && <div className="faq-answer">{faq.answer}</div>}
              </div>
            ))}
          </div>
        </section>

        <section className="trust-section">
          {TRUST_POINTS.map((point) => (
            <div key={point.title} className="trust-card">
              <div className="trust-icon" aria-hidden="true">{point.icon}</div>
              <h3>{point.title}</h3>
              <p>{point.description}</p>
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}
